import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { PanInfo } from 'motion/react'

import Image from 'next/image'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useState } from 'react'
import { testimonials } from '@/constants/content'
import { Reveal } from '@/components/generals/reveal'

const SOFT_EASE = [0.22, 1, 0.36, 1] as const
const BACKGROUND = '/images/lummi/testimonials.jpg'

/**
 * One glass card over a soft portrait. Quotes change with a gentle 3D turn
 * and fade, by the arrow buttons, the keyboard arrows or a sideways drag.
 */
export const Testimonials: FC = (): ReactNode => {
    const [[index, direction], setSlide] = useState<[number, number]>([0, 1])
    const count = testimonials.length
    const item = testimonials[index]

    const go = useCallback((step: 1 | -1) => {
        setSlide(([current]) => [(current + step + count) % count, step])
    }, [count])

    const handleDragEnd = (_: unknown, info: PanInfo) => {
        if (info.offset.x < -60 || info.velocity.x < -450) go(1)
        else if (info.offset.x > 60 || info.velocity.x > 450) go(-1)
    }

    const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault()
            go(1)
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            go(-1)
        }
    }

    return (
        <section id='testimonials' aria-labelledby='testimonials-title' className='relative overflow-hidden px-4 py-32 md:px-[30px] md:py-48'>
            <div aria-hidden='true' className='absolute inset-0'>
                <Image src={BACKGROUND} alt='' fill sizes='100vw' className='object-cover object-[50%_30%] opacity-70' />
                <div className='absolute inset-0 bg-black/40' />
                <div className='absolute inset-x-0 top-0 h-48 bg-linear-to-b from-background to-transparent' />
                <div className='absolute inset-x-0 bottom-0 h-48 bg-linear-to-t from-background to-transparent' />
            </div>

            <h2 id='testimonials-title' className='sr-only'>
                What clients say
            </h2>

            <Reveal y={36} className='relative mx-auto max-w-4xl'>
                <div
                    role='region'
                    aria-roledescription='carousel'
                    aria-label='Client testimonials'
                    tabIndex={0}
                    onKeyDown={handleKey}
                    className='rounded-[18px] border border-white/10 bg-black/35 px-6 py-12 text-center shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)] backdrop-blur-xl md:px-16 md:py-20'
                >
                    <div className='relative min-h-[15rem] [perspective:1400px] md:min-h-[16rem]'>
                        <AnimatePresence mode='wait' initial={false} custom={direction}>
                            <motion.figure
                                key={index}
                                custom={direction}
                                aria-roledescription='slide'
                                aria-label={`${index + 1} of ${count}`}
                                className='flex touch-pan-y select-none flex-col items-center'
                                data-cursor='Drag'
                                drag='x'
                                dragSnapToOrigin
                                dragElastic={0.2}
                                onDragEnd={handleDragEnd}
                                variants={{
                                    enter: (step: number) => ({ opacity: 0, rotateY: step * 28, x: step * 40, filter: 'blur(6px)' }),
                                    center: { opacity: 1, rotateY: 0, x: 0, filter: 'blur(0px)' },
                                    exit: (step: number) => ({ opacity: 0, rotateY: step * -28, x: step * -40, filter: 'blur(6px)' })
                                }}
                                initial='enter'
                                animate='center'
                                exit='exit'
                                transition={{ duration: 0.7, ease: SOFT_EASE }}
                            >
                                <blockquote className='max-w-[28ch] text-[clamp(1.5rem,2.8vw,2.6rem)] font-light leading-[1.2] tracking-[-0.03em] text-foreground'>
                                    {`“${item.quote}”`}
                                </blockquote>

                                <figcaption className='mt-10 text-sm'>
                                    <span className='block text-foreground'>{item.name}</span>
                                    <span className='mt-1 block text-muted-foreground'>{item.role}</span>
                                </figcaption>
                            </motion.figure>
                        </AnimatePresence>
                    </div>

                    <div className='mt-10 flex items-center justify-center gap-4'>
                        <button
                            type='button'
                            onClick={() => go(-1)}
                            className='flex size-12 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-ink'
                        >
                            <ArrowLeft className='size-4' aria-hidden='true' />

                            <span className='sr-only'>
                                Previous testimonial
                            </span>
                        </button>

                        <p className='w-16 text-center text-sm tabular-nums text-muted-foreground' aria-hidden='true'>
                            <span className='text-foreground'>{String(index + 1).padStart(2, '0')}</span>
                            {` / ${String(count).padStart(2, '0')}`}
                        </p>

                        <button
                            type='button'
                            onClick={() => go(1)}
                            className='flex size-12 items-center justify-center rounded-full border border-white/15 transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-ink'
                        >
                            <ArrowRight className='size-4' aria-hidden='true' />

                            <span className='sr-only'>
                                Next testimonial
                            </span>
                        </button>
                    </div>
                </div>
            </Reveal>

            <p className='sr-only' aria-live='polite'>
                {`Testimonial ${index + 1} of ${count}, from ${item.name}`}
            </p>
        </section>
    )
}
